import { assert } from '../../app/logger.js';

class Pipeable {
  pipe(receiver) {
    this.addReceiver('self', receiver);
    if (this.data !== undefined) {
      this.notifyReceiver(receiver, this.data);
    }
    return this.createPipe(receiver);
  }

  unpipe(receiver) {
    this.removeReceiver('self', receiver);
  }

  set(data) {
    let oldData = this.data;
    this.data = data;
    this.notifyIfChanged('self', this.data, oldData);
    return this;
  }

  clear() {
    this.set(this.constructor.DefaultValue);
  }

  get pipeables() {
    if (!this.#pipeables) {
      this.#pipeables = new this.constructor.Pipeables(this);
    }
    return this.#pipeables;
  }

  get pipeablesCached() {
    return this.#pipeables;
  }

  // protected

  createPipe(...args) {
    return {
      unpipe: () => this.unpipe(...args),
      closure: func => this.closure(func, ...args),
    };
  }

  hasReceivers(property) {
    if (!property) {
      return Array.from(this.#pipes.values()).some(
        receivers => receivers.length > 0
      );
    }
    let receivers = this.#pipes.get(property);
    return (receivers && receivers.length > 0) || false;
  }

  getReceivers(property) {
    return this.#pipes.get(property);
  }

  async closure(func, receiver) {
    assert(func, `[data] no function given`);
    assert(
      receiver instanceof Function,
      `[data] only function receiver can be scoped`
    );

    this.#inactiveReceivers.push(receiver);
    await func();
    this.#inactiveReceivers.splice(
      this.#inactiveReceivers.indexOf(receiver),
      1
    );
  }

  addReceiver(key, receiver) {
    assert(
      receiver instanceof Function,
      `[data] wrong receiver for ${key} key`
    );
    let receivers = this.#pipes.get(key);
    if (!receivers) {
      receivers = [];
      this.#pipes.set(key, receivers);
    }
    let receiverIndex = receivers.indexOf(receiver);
    if (receiverIndex == -1) {
      receivers.push(receiver);
    }
    return receiver;
  }

  removeReceiver(key, receiver) {
    let receivers = this.#pipes.get(key);
    assert(receivers, `[data] no piped receivers for ${key} key`);
    if (receivers) {
      let index = receivers.indexOf(receiver);
      assert(index != -1, `[data] no receiver is found for ${key} key`);
      if (index != -1) {
        receivers.splice(index, 1);
      }
    }
  }

  notifyIfChanged(property, value, oldValue) {
    if (value != oldValue) {
      this.notify(property, value);
    }
  }

  notify(property, value, ...args) {
    let receivers = this.getReceivers(property);
    if (receivers) {
      for (let receiver of receivers) {
        this.notifyReceiver(receiver, value, ...args);
      }
    }
  }

  notifyReceiver(receiver, value, ...args) {
    assert(receiver, `[data] no receiver`);
    if (this.#inactiveReceivers.indexOf(receiver) == -1) {
      receiver(value, ...args, this);
    }
  }

  get pipedValues() {
    return Array.from(this.#pipes.keys()).reduce(
      (a, p) => a.set(p, this[p]),
      new Map()
    );
  }

  static DefaultValue;
  static Pipeables;

  data = this.constructor.DefaultValue;

  #pipeables;
  #pipes = new Map();
  #inactiveReceivers = [];
}

export { Pipeable };
